import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardMedia from '@mui/material/CardMedia';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// third-party
import { enqueueSnackbar } from 'notistack';
import {
	CloseOutlined,
	CopyOutlined,
	DiscordFilled,
	FacebookFilled,
	InstagramFilled,
	LeftOutlined,
	LinkedinFilled,
	PictureOutlined,
	PlusOutlined,
	RightOutlined,
	SaveOutlined,
	ShareAltOutlined,
	WhatsAppOutlined,
	XOutlined
} from '@ant-design/icons';

// project imports
import MainCard from '@/components/MainCard';
import IconButton from '@/components/@extended/IconButton';
import TruncatedText from '@/components/TruncatedText';
import { PopupTransition } from '@/components/@extended/Transitions';
import { postFile } from '@/hooks/fetch-hooks/use-file-uploader/actions';
import { consumeTrackedAssetsByIds } from '@/store/assets/helpers';
import { ACCEPTED_IMAGE_EXTENSIONS, isAllowedImageFile } from '@/utils/file-upload';
import { createShare, getShareDetails } from './api';
import { useShareContext, buildShareUrl, SHARE_TIP_LABEL } from './context';

// ==============================|| CITYZEN - SHARE DIALOG (entity-agnostic, global) ||============================== //

type ShareUrl = ((url: string, text: string) => string) | null;

// brand identity colours (not theme tokens — brand assets). `build` primește linkul
// canonic + mesajul compus (titlu ± descriere) și întoarce intentul web cu postarea
// PRECOMPLETATĂ, în limitele fiecărei platforme; rețelele fără intent (Instagram/
// Discord) primesc `appUrl` — copiem mesajul gata de lipit și deschidem aplicația
// țintă (REV-SHR-02).
const NETWORKS: {
	id: string;
	label: string;
	Icon: typeof FacebookFilled;
	color: string;
	build: ShareUrl;
	appUrl?: string;
}[] = [
	{
		id: 'facebook',
		label: 'Facebook',
		Icon: FacebookFilled,
		color: '#1877F2',
		build: (u, t) =>
			`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}&quote=${encodeURIComponent(t)}`
	},
	{
		id: 'whatsapp',
		label: 'WhatsApp',
		Icon: WhatsAppOutlined,
		color: '#25D366',
		build: (u, t) => `https://wa.me/?text=${encodeURIComponent(`${t}\n${u}`)}`
	},
	{
		id: 'x',
		label: 'X',
		Icon: XOutlined,
		color: '#000000',
		build: (u, t) =>
			`https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}`
	},
	{
		id: 'linkedin',
		label: 'LinkedIn',
		Icon: LinkedinFilled,
		color: '#0A66C2',
		// share-offsite ignoră textul — compunerea de feed (shareActive) precompletează postarea
		build: (u, t) =>
			`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(`${t}\n${u}`)}`
	},
	{
		id: 'instagram',
		label: 'Instagram',
		Icon: InstagramFilled,
		color: '#E4405F',
		build: null,
		appUrl: 'https://www.instagram.com/'
	},
	{
		id: 'discord',
		label: 'Discord',
		Icon: DiscordFilled,
		color: '#5865F2',
		build: null,
		appUrl: 'https://discord.com/channels/@me'
	}
];

export default function ShareDialog() {
	const { target, closeShare } = useShareContext();

	const [titlu, setTitlu] = useState('');
	const [descriere, setDescriere] = useState('');
	const [image, setImage] = useState<string | undefined>(undefined);
	const [network, setNetwork] = useState('facebook');
	// linkul canonic vine din backend (GET api/shares/{type}/{id}); până sosește — sau
	// dacă entitatea nu e publicată — folosim fallback-ul client (buildShareUrl)
	const [backendLink, setBackendLink] = useState<string | null>(null);
	// poza personalizată SALVATĂ în BE (ogImageUrl) — candidată pe lângă cele ale entității
	const [savedImageUrl, setSavedImageUrl] = useState<string | null>(null);
	// poze urcate din dialog (api/assets) — candidate pe lângă cele ale entității.
	// Rămân în tracking-ul de orfane până când o distribuire le persistă (POST api/shares
	// cu ogImageId) — abia atunci le consumăm; altfel GC-ul de pe schimbarea de rută le
	// șterge din bucket (comportamentul corect pentru un upload abandonat).
	const [uploadedImages, setUploadedImages] = useState<string[]>([]);
	const [uploading, setUploading] = useState(false);
	// url → id de asset (din target.imageAssets, din GET api/shares și din upload) —
	// POST api/shares cere ogImageId; pozele fără id cunoscut nu pot fi persistate
	const [imageIdByUrl, setImageIdByUrl] = useState<Record<string, number>>({});
	// acțiunea în curs (Salvează/Copiază/Distribuie) — toate trec prin POST api/shares,
	// deci rulează una singură o dată, cu spinner pe butonul apăsat
	const [busyAction, setBusyAction] = useState<null | 'save' | 'copy' | 'share'>(null);
	const uploadInputRef = useRef<HTMLInputElement>(null);

	const open = Boolean(target);
	const fallbackLink = useMemo(() => (target ? buildShareUrl(target) : ''), [target]);
	const link = backendLink ?? fallbackLink;

	// mesajul gata de postat (titlu ± descriere) — intră precompletat în intentul rețelei
	const postText = useMemo(
		() => [titlu.trim(), descriere.trim()].filter(Boolean).join('\n\n'),
		[titlu, descriere]
	);

	// pozele candidate — cea salvată în BE + ale entității + cele urcate din dialog
	const imageOptions = useMemo(() => {
		if (!target) return [] as string[];
		const all = [
			...(savedImageUrl ? [savedImageUrl] : []),
			...(target.image ? [target.image] : []),
			...(target.images ?? []),
			...(target.imageAssets ?? []).map((a) => a.url),
			...uploadedImages
		];
		return Array.from(new Set(all));
	}, [target, savedImageUrl, uploadedImages]);

	// carusel „Poză": săgețile paginate banda de miniaturi (scrollbar ascuns);
	// starea ține activarea săgeților după poziția curentă de derulare
	const imageStripRef = useRef<HTMLDivElement>(null);
	const [stripNav, setStripNav] = useState({ left: false, right: false });
	const stripOverflows = stripNav.left || stripNav.right;

	const updateStripNav = () => {
		const el = imageStripRef.current;
		if (!el) return;
		setStripNav({
			left: el.scrollLeft > 1,
			right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1
		});
	};

	useEffect(() => {
		updateStripNav();
	}, [open, imageOptions]);

	const scrollStrip = (dir: 1 | -1) => {
		imageStripRef.current?.scrollBy({ left: dir * 240, behavior: 'smooth' });
	};

	useEffect(() => {
		if (open && target) {
			setTitlu(target.title);
			setDescriere(target.description ?? '');
			setImage(target.image ?? target.images?.[0] ?? target.imageAssets?.[0]?.url);
			setNetwork('facebook');
			setUploadedImages([]);
			setImageIdByUrl(
				Object.fromEntries((target.imageAssets ?? []).map((a) => [a.url, a.id]))
			);
		}
	}, [open, target]);

	// legare backend: la deschidere citim meta EFECTIV (personalizarea salvată sau
	// default-ul entității) + linkul canonic — GET api/shares e side-effect-free
	const targetTip = target?.tip;
	const targetId = target?.id;
	useEffect(() => {
		setBackendLink(null);
		setSavedImageUrl(null);
		if (!open || !targetTip || targetId === undefined) return undefined;
		let alive = true;
		getShareDetails(targetTip, targetId)
			.then((details) => {
				if (!alive) return;
				setBackendLink(details.url);
				if (details.ogTitle) setTitlu(details.ogTitle);
				if (details.ogDescription != null) setDescriere(details.ogDescription);
				if (details.ogImageUrl) {
					setSavedImageUrl(details.ogImageUrl);
					setImage(details.ogImageUrl);
					if (details.ogImageId != null) {
						const { ogImageId, ogImageUrl } = details;
						setImageIdByUrl((prev) => ({ ...prev, [ogImageUrl]: ogImageId }));
					}
				}
			})
			.catch(() => {
				// entitate nepublicată / endpoint indisponibil — rămân datele entității + linkul client
			});
		return () => {
			alive = false;
		};
	}, [open, targetTip, targetId]);

	if (!target) return null;

	const activeNetwork = NETWORKS.find((n) => n.id === network) ?? NETWORKS[0];

	// POST api/shares: înregistrează distribuirea și PERSISTĂ personalizarea (titlu/
	// descriere/poză) — valorile salvate ajung în OG tags-urile linkului (bridge).
	// ogImageId: id-ul pozei alese; fără id cunoscut → null (BE revine la poza default).
	// Toate cele trei acțiuni (Salvează / Copiază / Distribuie) trec pe aici.
	const persistShare = async (): Promise<{ url: string; saved: boolean }> => {
		try {
			const selectedImageId = image ? imageIdByUrl[image] : undefined;
			const details = await createShare(target.tip, target.id, {
				ogTitle: titlu.trim() || undefined,
				ogDescription: descriere.trim() || undefined,
				ogImageId: selectedImageId ?? null
			});
			setBackendLink(details.url);
			// poza e acum referită de share-ul din BE → scoasă din tracking-ul de orfane,
			// altfel GC-ul de pe schimbarea de rută o șterge din bucket
			if (selectedImageId !== undefined) consumeTrackedAssetsByIds([selectedImageId]);
			return { url: details.url, saved: true };
		} catch {
			// endpoint indisponibil — continuăm cu linkul existent, fără persistare
			return { url: link, saved: false };
		}
	};

	// o singură acțiune în zbor la un moment dat — butoanele se dezactivează între ele
	const runAction = async (
		action: 'save' | 'copy' | 'share',
		fn: () => Promise<void>
	) => {
		if (busyAction) return;
		setBusyAction(action);
		try {
			await fn();
		} finally {
			setBusyAction(null);
		}
	};

	// Salvează: doar persistă personalizarea (aceeași logică — POST api/shares)
	const handleSaveOnly = async () => {
		const { saved } = await persistShare();
		if (saved) enqueueSnackbar('Personalizarea a fost salvată.', { variant: 'success' });
		else enqueueSnackbar('Nu am reușit să salvăm personalizarea.', { variant: 'error' });
	};

	// Copiază = salvează + copiază linkul canonic
	const handleCopy = async () => {
		const { url, saved } = await persistShare();
		try {
			await navigator.clipboard.writeText(url);
			enqueueSnackbar(
				saved
					? 'Link copiat — personalizarea a fost salvată.'
					: 'Link copiat — personalizarea nu a putut fi salvată.',
				{ variant: saved ? 'success' : 'warning' }
			);
		} catch {
			/* clipboard unavailable */
		}
	};

	const handleShare = async () => {
		const { url: freshLink } = await persistShare();
		if (activeNetwork.build) {
			window.open(
				activeNetwork.build(freshLink, postText),
				'_blank',
				'noopener,noreferrer'
			);
			return;
		}
		// Instagram/Discord nu au intent web de share — copiem MESAJUL gata de postat
		// (titlu + descriere + link) și deschidem aplicația țintă
		if (activeNetwork.appUrl) {
			try {
				await navigator.clipboard.writeText(`${postText}\n${freshLink}`);
			} catch {
				/* clipboard unavailable */
			}
			window.open(activeNetwork.appUrl, '_blank', 'noopener,noreferrer');
			enqueueSnackbar(
				`Mesajul e copiat — lipește-l în ${activeNetwork.label} pentru a distribui.`,
				{ variant: 'info' }
			);
			return;
		}
		if (typeof navigator !== 'undefined' && navigator.share) {
			navigator.share({ title: titlu, text: descriere, url: freshLink }).catch(() => {});
			return;
		}
		// fallback teoretic (toate rețelele au build/appUrl): copiem linkul deja persistat
		try {
			await navigator.clipboard.writeText(freshLink);
		} catch {
			/* clipboard unavailable */
		}
		enqueueSnackbar(
			`${activeNetwork.label} nu permite distribuirea directă — link copiat.`,
			{ variant: 'info' }
		);
	};

	// upload de poză nouă (api/assets) — aceeași validare de tip ca restul aplicației
	const handleUploadImage = async (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) return;
		if (!isAllowedImageFile(file)) {
			enqueueSnackbar('Format neacceptat — alege o imagine (png, jpg, jpeg, gif, bmp).', {
				variant: 'error'
			});
			return;
		}
		setUploading(true);
		try {
			const uploaded = await postFile(file);
			const url = uploaded.publicUrl ?? uploaded.url;
			setUploadedImages((prev) => (prev.includes(url) ? prev : [...prev, url]));
			setImageIdByUrl((prev) => ({ ...prev, [url]: uploaded.id }));
			setImage(url);
		} catch (err) {
			enqueueSnackbar(
				err instanceof Error ? err.message : 'Nu am reușit să încărcăm imaginea.',
				{ variant: 'error' }
			);
		} finally {
			setUploading(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={closeShare}
			maxWidth="md"
			fullWidth
			slots={{ transition: PopupTransition }}
			aria-labelledby="share-title"
		>
			<DialogTitle id="share-title" sx={{ p: 2.5 }}>
				<Stack
					direction="row"
					sx={{ gap: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}
				>
					<Stack>
						<Typography variant="h4">Distribuie {SHARE_TIP_LABEL[target.tip]}</Typography>
						<Typography variant="body2" sx={{ color: 'text.secondary' }}>
							Alege rețeaua, personalizează cum apare și distribuie — sau copiază linkul.
						</Typography>
					</Stack>
					<IconButton
						color="secondary"
						onClick={closeShare}
						aria-label="Închide"
						sx={{ mt: -0.5, mr: -0.5 }}
					>
						<CloseOutlined />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent dividers sx={{ p: 2.5 }}>
				<Stack sx={{ gap: 2.5 }}>
					{/* 1. choose network */}
					<Stack sx={{ gap: 1 }}>
						<Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
							Rețea
						</Typography>
						<ToggleButtonGroup
							exclusive
							value={network}
							onChange={(_e, v) => v && setNetwork(v)}
							sx={{ flexWrap: 'wrap' }}
						>
							{NETWORKS.map(({ id, label, Icon, color }) => (
								<ToggleButton key={id} value={id} aria-label={label} sx={{ px: 2 }}>
									<Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
										<Box
											component="span"
											sx={{ display: 'inline-flex', fontSize: '1.15rem', color }}
										>
											<Icon />
										</Box>
										<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
											{label}
										</Box>
									</Stack>
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</Stack>

					<Box
						sx={{
							display: 'grid',
							// minmax(0, 1fr): coloanele `1fr` au implicit min-width auto — banda
							// derulabilă de poze ar lărgi coloana Detalii și ar strivi preview-ul
							gridTemplateColumns: {
								xs: 'minmax(0, 1fr)',
								md: 'minmax(0, 1fr) minmax(0, 1fr)'
							},
							gap: 3
						}}
					>
						{/* 2. edit details */}
						<Stack sx={{ gap: 2, minWidth: 0 }}>
							<Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
								Detalii
							</Typography>
							<TextField
								label="Titlu"
								value={titlu}
								onChange={(e) => setTitlu(e.target.value)}
								fullWidth
							/>
							<TextField
								label="Descriere"
								value={descriere}
								onChange={(e) => setDescriere(e.target.value)}
								fullWidth
								multiline
								minRows={3}
							/>
							{/* poza distribuirii — selectabilă dintre imaginile entității (REV-SHR-01)
									sau urcată direct din dialog (api/assets) */}
							<Stack sx={{ gap: 0.75 }}>
								<Typography variant="caption" sx={{ color: 'text.secondary' }}>
									Poză
								</Typography>
								{/* carusel: săgeți stânga/dreapta paginate banda (scrollbar ascuns);
										tile-ul de upload rămâne mereu la capăt */}
								<Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
									{stripOverflows && (
										<IconButton
											size="small"
											color="secondary"
											aria-label="Pozele anterioare"
											disabled={!stripNav.left}
											onClick={() => scrollStrip(-1)}
										>
											<LeftOutlined />
										</IconButton>
									)}
									<Stack
										ref={imageStripRef}
										direction="row"
										onScroll={updateStripNav}
										sx={{
											gap: 1,
											flex: 1,
											minWidth: 0,
											overflowX: 'auto',
											scrollbarWidth: 'none',
											'&::-webkit-scrollbar': { display: 'none' },
											'& > *': { flexShrink: 0 }
										}}
									>
										{imageOptions.map((src) => (
											<Box
												key={src}
												component="button"
												type="button"
												aria-label="Alege poza"
												onClick={() => setImage(src)}
												sx={{
													p: 0.25,
													borderRadius: 1,
													border: '2px solid',
													borderColor: image === src ? 'primary.main' : 'divider',
													bgcolor: 'transparent',
													cursor: 'pointer'
												}}
											>
												<CardMedia
													image={src}
													sx={{ width: 96, height: 64, borderRadius: 0.75 }}
												/>
											</Box>
										))}
										<Box
											component="button"
											type="button"
											aria-label="Încarcă o poză nouă"
											disabled={uploading}
											onClick={() => uploadInputRef.current?.click()}
											sx={{
												p: 0.25,
												width: 100,
												height: 68.5,
												borderRadius: 1,
												border: '2px dashed',
												borderColor: 'divider',
												bgcolor: 'transparent',
												color: 'text.secondary',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												cursor: uploading ? 'default' : 'pointer',
												'&:hover': { borderColor: 'primary.main', color: 'primary.main' }
											}}
										>
											{uploading ? (
												<CircularProgress size={18} />
											) : (
												<Stack sx={{ gap: 0.25, alignItems: 'center' }}>
													<PlusOutlined />
													<Typography variant="caption">Încarcă</Typography>
												</Stack>
											)}
										</Box>
										<input
											ref={uploadInputRef}
											type="file"
											accept={ACCEPTED_IMAGE_EXTENSIONS}
											hidden
											onChange={handleUploadImage}
										/>
									</Stack>
									{stripOverflows && (
										<IconButton
											size="small"
											color="secondary"
											aria-label="Pozele următoare"
											disabled={!stripNav.right}
											onClick={() => scrollStrip(1)}
										>
											<RightOutlined />
										</IconButton>
									)}
								</Stack>
							</Stack>
						</Stack>

						{/* 3. live preview */}
						<Stack sx={{ gap: 1, minWidth: 0 }}>
							<Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
								Previzualizare — {activeNetwork.label}
							</Typography>
							<MainCard content={false}>
								{image ? (
									<CardMedia image={image} sx={{ height: 168 }} />
								) : (
									<Box
										sx={{
											height: 168,
											bgcolor: 'secondary.lighter',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											color: 'text.secondary'
										}}
									>
										<PictureOutlined style={{ fontSize: '1.75rem' }} />
									</Box>
								)}
								<Box sx={{ p: 2, bgcolor: 'background.default' }}>
									<Typography
										variant="caption"
										sx={{ color: 'text.secondary', wordBreak: 'break-all' }}
									>
										{link}
									</Typography>
									<Typography variant="subtitle1" sx={{ mt: 0.5 }}>
										{titlu || `Titlu ${SHARE_TIP_LABEL[target.tip]}`}
									</Typography>
									<TruncatedText
										variant="body2"
										lines={2}
										sx={{ color: 'text.secondary' }}
									>
										{descriere || `Descrierea ${SHARE_TIP_LABEL[target.tip]}ului…`}
									</TruncatedText>
								</Box>
							</MainCard>
						</Stack>
					</Box>

					{/* 4. link with inline copy */}
					<Stack sx={{ gap: 1 }}>
						<Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
							Link de distribuire
						</Typography>
						<TextField
							fullWidth
							value={link}
							slotProps={{
								input: {
									readOnly: true,
									endAdornment: (
										<InputAdornment position="end">
											<Tooltip title="Copiază linkul">
												<span>
													<IconButton
														color="secondary"
														disabled={!!busyAction}
														onClick={() => runAction('copy', handleCopy)}
														aria-label="Copiază linkul"
													>
														<CopyOutlined />
													</IconButton>
												</span>
											</Tooltip>
										</InputAdornment>
									)
								}
							}}
						/>
					</Stack>
				</Stack>
			</DialogContent>

			<DialogActions sx={{ p: 2.5 }}>
				<Button
					color="secondary"
					variant="outlined"
					startIcon={<SaveOutlined />}
					disabled={!!busyAction}
					onClick={() => runAction('save', handleSaveOnly)}
				>
					{busyAction === 'save' ? <CircularProgress size={18} /> : 'Salvează'}
				</Button>
				<Button
					color="secondary"
					variant="outlined"
					startIcon={<CopyOutlined />}
					disabled={!!busyAction}
					onClick={() => runAction('copy', handleCopy)}
				>
					{busyAction === 'copy' ? <CircularProgress size={18} /> : 'Copiază linkul'}
				</Button>
				<Button
					variant="contained"
					startIcon={<ShareAltOutlined />}
					disabled={!!busyAction}
					onClick={() => runAction('share', handleShare)}
				>
					{busyAction === 'share' ? (
						<CircularProgress size={18} />
					) : (
						`Distribuie pe ${activeNetwork.label}`
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
